import {
  Action,
  combineReducers,
  createFeatureSelector,
  createSelector,
} from '@ngrx/store';
import * as fromInitReleaseTableState from './init-release-table-state.reducer';
import * as fromInRTransactions from './init-release-transactions.reducer';
import * as fromCreateRuleTableState from './create-rule-table-state.reducer';
import { selectCreateStandingAuthtEauthClientCallSucessfullyCompleted, selectEauthClientCallSucessfullyCompleted } from '../../setup-eAuthorization/reducers';
import { selectAccountInfoSuccessfullyCompleted, selectLoadTransactionsCallFinished, selectMasterAccount, selectSubAccountId, selectUserContext } from '../../account-info-header/reducers';
import { ContextType } from '@charlesschwab/as-ui-core';

export const initReleaseFeatureKey = 'initReleaseState';

export interface InitReleaseState {
  [fromInitReleaseTableState.initReleaseTableStateKey]: fromInitReleaseTableState.State;
  [fromInRTransactions.initReleaseTransactionKey]: fromInRTransactions.State;
  [fromCreateRuleTableState.createRuleTableStateKey]: fromCreateRuleTableState.State;
}

export function reducers(state: InitReleaseState | undefined, action: Action) {
  return combineReducers({
    [fromInitReleaseTableState.initReleaseTableStateKey]:
      fromInitReleaseTableState.initReleaseTableStateReducer,
    [fromInRTransactions.initReleaseTransactionKey]:
      fromInRTransactions.initReleaseTransactionsReducer,
    [fromCreateRuleTableState.createRuleTableStateKey]:
      fromCreateRuleTableState.createRuleTableStateReducer,
  })(state, action);
}

export const selectInitReleaseState = createFeatureSelector<InitReleaseState>(
  initReleaseFeatureKey
);

export const selectInitReleaseTableState = createSelector(
  selectInitReleaseState,
  (state) => {
    return state.initReleaseTableState;
  }
);

export const selectCreateRuleTableState = createSelector(
  selectInitReleaseState,
  (state) => {
    return state.createRuleTableState;
  }
);

export const selectInitReleaseTableUpdatedState = createSelector(
  selectInitReleaseTableState,
  selectEauthClientCallSucessfullyCompleted,
  selectAccountInfoSuccessfullyCompleted,
  selectMasterAccount,
  selectSubAccountId,
  selectLoadTransactionsCallFinished,
  (initReleaseState, eauthCallCompleted, accountInfoCallCompleted, masterAcct, subAcct, overviewCallCompleted) => {
    // in master acct table view we haven't called eauth and acctInfo yet
    return ((eauthCallCompleted && accountInfoCallCompleted && overviewCallCompleted) || (masterAcct && !subAcct))
      ? initReleaseState
      : { ...initReleaseState, loading: true};
  }
);

export const selectInitReleaseTransactionTable = createSelector(
  selectInitReleaseState,
  (state) => {
    return state.inrTransactionsState;
  }
);

export const selectCreateCustomRuleSection = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.createCustomRuleSection;
  }
);

export const selectCreateCustomRuleSectionRulesList = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.getCustomRuleSection;
  } 
);

export const selectCallCustomRulesPostSuccess = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.initReleaseCustomRulePostSuccess;
  }
);

export const selectCallCustomRulesPostFailed = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.initReleaseCustomRulePostFailed;
  }
);

export const selectCallCustomRulesGetSuccess = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.initReleaseCustomRuleGetSuccess;
  }
);

export const selectCallCustomRulesGetFailed = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.initReleaseCustomRuleGetFailed;
  }
);

export const selectCallCustomRulesDeleteSuccess = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.initReleaseCustomRuleDeleteSuccess;
  }
);

export const selectCallCustomRulesDeleteFailed = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.initReleaseCustomRuleDeleteFailed;
  }
);

export const selectCustomRuleType = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.createCustomRuleSection.ruleType;
  }
);

export const selectInitReleaseTransactionTableReadyToLoad = createSelector(
  selectInitReleaseState,
  selectEauthClientCallSucessfullyCompleted,
  selectAccountInfoSuccessfullyCompleted,
  selectUserContext,
  selectCreateStandingAuthtEauthClientCallSucessfullyCompleted,
  selectLoadTransactionsCallFinished,
  (initReleaseState, eauthCallCompleted, accountInfoCallCompleted, context, sloaEauthCallCompleted, overViewCallFinished) => {
    return (context === ContextType.Master) ? initReleaseState.inrTransactionsState : eauthCallCompleted && accountInfoCallCompleted && sloaEauthCallCompleted && overViewCallFinished
      ? initReleaseState.inrTransactionsState
      : null;
  }
);

export const selectInitReleaseTransactionDeleteCallSuccessful = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return (
      state.initReleaseTransactionDeleteCallCompleted &&
      !state.initReleaseTransactionDeleteCallFailed
    );
  }
);

export const selectInitReleaseTransactionDeleteCallCompleted = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return (
      state.initReleaseTransactionDeleteCallCompleted
    );
  }
);

export const selectInitReleaseTransactionDeleteCallFailure = createSelector(
  selectInitReleaseState,
  (state) => {
    return (
      state.inrTransactionsState.initReleaseTransactionDeleteCallFailed &&
      state.inrTransactionsState.initReleaseTransactionDeleteCallCompleted
    );
  }
);

export const selectInitReleaseTransactionsByDraftIdCallCompleted = createSelector(
  selectInitReleaseState,
  (state) => {
    return (
      state.inrTransactionsState.initReleaseTransactionsByDraftIdCallCompleted
    );
  }
);

export const selectInitReleaseTransactionsByDraftIdCallSuccessful = createSelector(
  selectInitReleaseState,
  (state) => {
    return (
      state.inrTransactionsState.initReleaseTransactionsByDraftIdCallSuccess
    );
  }
);

export const selectInitReleaseTransactionsByDraftIdCallStarted = createSelector(
  selectInitReleaseState,
  (state) => {
    return (
      state.inrTransactionsState.initReleaseTransactionsByDraftIdCallStarted
    );
  }
);

export const selectInitReleaseTransactionsByDraftIdCallSuccess = createSelector(selectInitReleaseTransactionTable, (state) => {
  return state.initReleaseTransactionsByDraftIdCallSuccess;
});

export const selectInitReleaseTransactionInContext = createSelector(selectInitReleaseTransactionTable, (state) => {
  return state.inrTransactionInContext;
});

export const selectInitReleaseInitiatedBy = createSelector(selectInitReleaseTransactionInContext, (state) => {
  return state.createdByLoginId;
});

export const selectInitReleaseTransactionNickname = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.inrTransactionInContext.sloaAccountData.allSLOAList.map(item => ({nickName:item.cashieringRqstID.toString() === state.inrTransactionInContext.draftObject.cashieringRequestId ? item.nickName : ''}))[0] ?
           state.inrTransactionInContext.sloaAccountData.allSLOAList.map(item => ({nickName:item.cashieringRqstID.toString() === state.inrTransactionInContext.draftObject.cashieringRequestId ? item.nickName : ''}))[0].nickName : '';
  }
);

export const selectSloaDataForTransactionBeingReleased = createSelector(
  selectInitReleaseTransactionTable,
  (state) => {
    return state.inrTransactionInContext.sloaAccountData?.allSLOAList?.filter(item => item.cashieringRqstID.toString() === state.inrTransactionInContext.draftObject.cashieringRequestId?.toString())[0] ?? '';
  }
);

export const selectInitReleaseTransactionIsSloaFlow = createSelector(selectInitReleaseTransactionTable, (state) => {
  return state.isSloa;
})
